<script setup lang="ts">
import {
  readUserMe,
  updateUserMe,
  type ContractUserShareInDb,
  type ContractualPartyUserShareInDb,
  type GoogleDriveIntegrationStatus,
  type PermissionRoleInDb,
  type UserInDb,
} from '@/api';
import { NIcon, type DataTableColumns } from 'naive-ui';
import { computed, h, onMounted, ref, type VNode } from 'vue';
import { RouterLink, useRoute, useRouter, type RouteLocationRaw } from 'vue-router';
import {
  IconDocumentText,
  IconEdit,
  IconGoogle,
  IconPeople,
  IconShieldCheck,
} from '@/components/design/icons';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import { gaiaContentWidth } from '@/components/design/themes/tokens';
import GaiaAvatar from '@/components/design/base/GaiaAvatar.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCard from '@/components/design/base/GaiaCard.vue';
import GaiaCreateButton from '@/components/design/base/GaiaCreateButton.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaStatusChip from '@/components/design/base/GaiaStatusChip.vue';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaInlineEdit from '@/components/design/base/inputs/GaiaInlineEdit.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaPopconfirm from '@/components/design/composite/GaiaPopconfirm.vue';
import GaiaTabBar, { type GaiaTabBarItem } from '@/components/design/composite/GaiaTabBar.vue';
import VaultSharesTable from '@/components/file_vault/VaultSharesTable.vue';
import OrganisationMembershipList from '@/components/design/domain/organisation/OrganisationMembershipList.vue';
import type { OrganisationOption } from '@/components/design/domain/organisation/organisationOption';
import GoogleDriveVerificationNoticeModal from '@/components/GoogleDriveVerificationNoticeModal.vue';
import CreateOrganisationModal from '@/components/organisation/CreateOrganisationModal.vue';
import OrganisationSeveranceModal from '@/components/organisation/OrganisationSeveranceModal.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { useListQuerySync } from '@/composables/useListQuerySync';
import { ContractShareService } from '@/services/contractShareService';
import { ContractualPartyShareService } from '@/services/contractualPartyShareService';
import {
  completeGoogleDriveOAuth,
  disconnectGoogleDriveIntegration,
  fetchGoogleDriveIntegrationStatus,
  requestGoogleDriveAuthorizationUrl,
} from '@/services/integration/googleDriveIntegrationService';
import {
  INTEGRATION_OAUTH_SCOPE_KEY,
  setIntegrationOauthContext,
} from '@/services/integration/integrationOauthService';
import { PermissionsService } from '@/services/permissionsService';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organizationStore';
import { useTourStore } from '@/stores/tourStore';
import { formatAccessPermissions } from '@/utils/accessPermission';
import { formatDateForDisplay } from '@/utils/datetimeUtils';
import { extractErrorDetail } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';
import {
  contractRoute as contractDetailRoute,
  contractualPartyRoute,
  permissionRoleRoute,
} from '@/router/links';

/**
 * Your own profile (/users/me): who you are, your integrations, what you hold
 * in the active organisation, and the organisations you belong to. The same
 * shape as an admin's view of a member (MemberDetailView) — header, tabs, one
 * list at a time — but read-only apart from your name and your own settings.
 */

type ProfileTab = 'profile' | 'roles' | 'contracts' | 'parties' | 'files' | 'organisations';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const organizationStore = useOrganizationStore();
const message = useGaiaToast();

// The OAuth return leg lands without a tab, so it opens on Profile, where the integrations live.
const { activeTab } = useListQuerySync({
  defaultTab: 'profile',
  allowedTabs: ['profile', 'roles', 'contracts', 'parties', 'files', 'organisations'],
});

const user = ref<UserInDb | null>(null);
const loading = ref(false);

async function fetchUser(): Promise<void> {
  loading.value = true;
  try {
    const { data } = await readUserMe({ throwOnError: true });
    user.value = data;
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to load user');
    user.value = null;
  } finally {
    loading.value = false;
  }
}

// ==================== Header ====================

/** An empty name falls back to the email, so this is a truthiness check on purpose. */
const displayName = computed(() => {
  if (!user.value) return 'You';
  return user.value.full_name.trim() || user.value.email;
});

const initials = computed(() => {
  const source = displayName.value;
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  return (parts.length > 1 ? parts[0][0] + parts[1][0] : source.slice(0, 2)).toUpperCase();
});

// The one thing a user may change about themselves: the backend's UserUpdate
// carries `full_name` only. The email is identity and stays read-only.
const editingName = ref(false);
const savingName = ref(false);

async function commitName(fullName: string): Promise<void> {
  editingName.value = false;
  if (!user.value || savingName.value) return;
  savingName.value = true;
  try {
    const response = await updateUserMe({ body: { full_name: fullName } });
    if (response.error) {
      throw new Error(extractErrorDetail(response.error, 'Failed to update your name'));
    }
    user.value = { ...user.value, full_name: response.data.full_name };
    // Home greets by this name and the sidebar shows its initials, so the
    // profile the app runs on has to learn the new one too.
    if (authStore.backendUser) {
      authStore.backendUser = { ...authStore.backendUser, full_name: response.data.full_name };
    }
    message.success('Name updated');
  } catch (error) {
    logger.error('Failed to update name:', error);
    message.error(extractErrorDetail(error, 'Failed to update your name'));
  } finally {
    savingName.value = false;
  }
}

// ==================== Google Drive (user scope) ====================

const googleDriveStatus = ref<GoogleDriveIntegrationStatus | null>(null);
const googleDriveLoading = ref(false);
const googleDriveActionLoading = ref(false);
const googleDriveNoticeModal = ref<InstanceType<typeof GoogleDriveVerificationNoticeModal>>();

const isGoogleDriveConnected = computed(() => googleDriveStatus.value?.connected === true);

async function fetchUserGoogleDriveStatus(): Promise<void> {
  googleDriveLoading.value = true;
  try {
    googleDriveStatus.value = await fetchGoogleDriveIntegrationStatus('user');
  } catch (error) {
    logger.error('Failed to fetch user Google Drive status:', error);
    googleDriveStatus.value = null;
  } finally {
    googleDriveLoading.value = false;
  }
}

function getQueryValue(key: string): string {
  const queryValue = route.query[key];
  if (Array.isArray(queryValue)) {
    return queryValue[0] ?? '';
  }
  return queryValue ?? '';
}

function isTruthyQueryValue(value: string): boolean {
  const normalized = value.toLowerCase();
  return ['1', 'true', 'ok', 'success', 'connected'].includes(normalized);
}

async function handleProfileOauthReturnFeedback(): Promise<void> {
  const integration = getQueryValue('integration').toLowerCase();
  const status = getQueryValue('status');
  const result = getQueryValue('result');
  const googleFlag = getQueryValue('google_drive');
  const code = getQueryValue('code');
  const oauthError = getQueryValue('error');
  const queryScope = getQueryValue('scope').toLowerCase();
  const storedScope = window.sessionStorage.getItem(INTEGRATION_OAUTH_SCOPE_KEY);
  const oauthScope =
    queryScope === 'user' || queryScope === 'organisation'
      ? queryScope
      : storedScope === 'user' || storedScope === 'organisation'
        ? storedScope
        : 'user';

  const hasOauthMarker =
    'code' in route.query ||
    'state' in route.query ||
    'integration' in route.query ||
    'status' in route.query ||
    'result' in route.query ||
    'google_drive' in route.query ||
    'scope' in route.query ||
    'error' in route.query;

  const isGoogleOauthFlow =
    integration === 'google_drive' ||
    integration === 'google-drive' ||
    googleFlag.length > 0 ||
    code.length > 0 ||
    oauthError.length > 0;

  if (isGoogleOauthFlow && oauthError) {
    message.error(`Google Drive OAuth failed: ${oauthError}`);
  }

  if (isGoogleOauthFlow && code && oauthScope === 'user') {
    try {
      googleDriveStatus.value = await completeGoogleDriveOAuth(code, 'user');
      message.success('Your Google Drive connected successfully.');
    } catch (error) {
      logger.error('Failed to complete user Google Drive OAuth:', error);
      message.error(extractErrorDetail(error, 'Failed to complete Google Drive OAuth.'));
    }
  }

  if (
    isGoogleOauthFlow &&
    !code &&
    !oauthError &&
    (isTruthyQueryValue(status) || isTruthyQueryValue(result) || isTruthyQueryValue(googleFlag))
  ) {
    message.success('Your Google Drive connected successfully.');
  }

  if (hasOauthMarker) {
    const nextQuery = { ...route.query };
    delete nextQuery.code;
    delete nextQuery.state;
    delete nextQuery.integration;
    delete nextQuery.status;
    delete nextQuery.result;
    delete nextQuery.google_drive;
    delete nextQuery.scope;
    delete nextQuery.error;
    await router.replace({ query: nextQuery }).catch(() => undefined);
  }

  window.sessionStorage.removeItem(INTEGRATION_OAUTH_SCOPE_KEY);
}

async function handleUserGoogleDriveConnect(): Promise<void> {
  googleDriveActionLoading.value = true;
  try {
    setIntegrationOauthContext({
      provider: 'google_drive',
      origin: 'profile_user',
      scope: 'user',
    });
    const authorizationUrl = await requestGoogleDriveAuthorizationUrl('user');
    window.location.href = authorizationUrl;
  } catch (error) {
    logger.error('Failed to start user Google Drive OAuth flow:', error);
    message.error(extractErrorDetail(error, 'Could not start Google Drive connection.'));
  } finally {
    googleDriveActionLoading.value = false;
  }
}

async function handleUserGoogleDriveDisconnect(): Promise<void> {
  googleDriveActionLoading.value = true;
  try {
    googleDriveStatus.value = await disconnectGoogleDriveIntegration('user');
    message.success('Your Google Drive disconnected successfully.');
  } catch (error) {
    logger.error('Failed to disconnect user Google Drive:', error);
    message.error(extractErrorDetail(error, 'Failed to disconnect Google Drive.'));
  } finally {
    googleDriveActionLoading.value = false;
  }
}

// ==================== Tutorials ====================

const tourStore = useTourStore();
const tutorialResetLoading = ref(false);

async function handleResetTutorials(): Promise<void> {
  tutorialResetLoading.value = true;
  try {
    await tourStore.resetAll();
    message.success('Tutorials reset — tours will show again when you visit their pages');
  } catch (error) {
    logger.error('Failed to reset tutorials:', error);
    message.error('Failed to reset tutorials');
  } finally {
    tutorialResetLoading.value = false;
  }
}

// ==================== Roles and shares ====================

const roles = ref<PermissionRoleInDb[]>([]);
const loadingRoles = ref(false);

async function fetchRoles(): Promise<void> {
  loadingRoles.value = true;
  try {
    roles.value = await PermissionsService.getOwnRoles();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to load your roles');
    roles.value = [];
  } finally {
    loadingRoles.value = false;
  }
}

const sharedContracts = ref<ContractUserShareInDb[]>([]);
const loadingContracts = ref(false);

async function fetchSharedContracts(uid: string): Promise<void> {
  loadingContracts.value = true;
  try {
    sharedContracts.value = await ContractShareService.getContractSharesByUser(uid);
  } catch (error) {
    message.error(
      error instanceof Error ? error.message : 'Failed to load contracts shared with you'
    );
    sharedContracts.value = [];
  } finally {
    loadingContracts.value = false;
  }
}

const sharedParties = ref<ContractualPartyUserShareInDb[]>([]);
const loadingParties = ref(false);

async function fetchSharedParties(uid: string): Promise<void> {
  loadingParties.value = true;
  try {
    sharedParties.value = await ContractualPartyShareService.getPartySharesByUser(uid);
  } catch (error) {
    message.error(
      error instanceof Error ? error.message : 'Failed to load parties shared with you'
    );
    sharedParties.value = [];
  } finally {
    loadingParties.value = false;
  }
}

// ==================== Organisations ====================

const showCreateOrganisation = ref(false);
const busyOrganisationUid = ref<string | null>(null);
const organisationPendingLeave = ref<OrganisationOption | null>(null);
const leaving = ref(false);
const leaveError = ref<string | null>(null);

// Same landing as the sidebar's switcher: a switch re-scopes every request,
// so a full document load onto home drops the cached state of the old
// organisation.
async function switchToOrganisation(uid: string): Promise<void> {
  if (busyOrganisationUid.value) return;
  busyOrganisationUid.value = uid;
  const switched = await organizationStore.switchCurrentUserOrganization(uid);
  if (!switched) {
    busyOrganisationUid.value = null;
    message.error(organizationStore.error ?? 'Failed to switch organisation');
    return;
  }
  window.location.assign('/');
}

function askToLeave(uid: string): void {
  leaveError.value = null;
  organisationPendingLeave.value = authStore.organisations.find(o => o.uid === uid) ?? null;
}

async function confirmLeave(): Promise<void> {
  const organisation = organisationPendingLeave.value;
  if (!organisation || leaving.value) return;
  const wasActive = authStore.backendUser?.active_organisation?.uid === organisation.uid;
  leaving.value = true;
  leaveError.value = null;

  const profile = await organizationStore.leaveOrganization(organisation.uid);
  if (!profile) {
    leaving.value = false;
    leaveError.value = organizationStore.error ?? 'Failed to leave organisation';
    return;
  }

  if (wasActive) {
    // The active organisation changed (to the next one, or to none); every
    // cached store is scoped to the old one, so start over on home.
    window.location.assign('/');
    return;
  }
  leaving.value = false;
  organisationPendingLeave.value = null;
  message.success(`You left ${organisation.name}`);
}

// ==================== Tabs ====================

/** Reported by the Files & folders table once it has loaded. */
const sharedVaultItemCount = ref<number | null>(null);

const tabs = computed((): GaiaTabBarItem[] => [
  { key: 'profile', label: navLabels.profile },
  { key: 'roles', label: 'Roles', count: roles.value.length },
  { key: 'contracts', label: 'Contracts', count: sharedContracts.value.length },
  { key: 'parties', label: 'Parties', count: sharedParties.value.length },
  { key: 'files', label: 'Files & folders', count: sharedVaultItemCount.value ?? undefined },
  { key: 'organisations', label: navLabels.organisations, count: authStore.organisations.length },
]);

const currentTab = computed(
  (): ProfileTab =>
    (tabs.value.some(tab => tab.key === activeTab.value)
      ? activeTab.value
      : 'profile') as ProfileTab
);

// ==================== Table cells ====================

/**
 * A bold first line with an optional muted second one. It links to what the
 * row lists when there is somewhere to go, so cmd- and middle-click open it in
 * a new tab.
 */
function renderTitle(
  to: RouteLocationRaw | null,
  primary: string,
  secondary?: string | null,
  tag?: string
): VNode {
  const title = h(GaiaText, { size: 'body', weight: 'semibold', tone: 'ink' }, () => primary);
  return h('div', { style: { display: 'flex', flexDirection: 'column', minWidth: 0 } }, [
    h('div', { style: { display: 'flex', alignItems: 'center', gap: '8px' } }, [
      to
        ? h(
            RouterLink,
            {
              to,
              style: 'color: inherit; text-decoration: none',
              onClick: (e: MouseEvent) => {
                e.stopPropagation();
              },
            },
            { default: () => title }
          )
        : title,
      tag ? h(GaiaTag, { variant: 'muted', size: 'small' }, () => tag) : null,
    ]),
    secondary ? h(GaiaText, { size: 'control', tone: 'subtle' }, () => secondary) : null,
  ]);
}

function renderAccess(bitmask: number): VNode {
  return h(GaiaTag, { variant: 'muted', size: 'small' }, () => formatAccessPermissions(bitmask));
}

/** Role pages are org-admin only, so only an admin's role rows lead anywhere. */
function roleRoute(role: PermissionRoleInDb): RouteLocationRaw | null {
  return authStore.userPermissions.is_org_admin ? permissionRoleRoute(role.uid) : null;
}

function contractRoute(row: ContractUserShareInDb): RouteLocationRaw {
  return contractDetailRoute(row.contract.uid);
}

function partyRoute(row: ContractualPartyUserShareInDb): RouteLocationRaw {
  return contractualPartyRoute(row.contractual_party.uid);
}

const roleColumns: DataTableColumns<PermissionRoleInDb> = [
  {
    title: 'Role',
    key: 'name',
    render: row =>
      renderTitle(
        roleRoute(row),
        row.name,
        row.description,
        row.protected ? 'Built-in' : undefined
      ),
  },
];

const contractColumns: DataTableColumns<ContractUserShareInDb> = [
  {
    title: 'Contract',
    key: 'contract',
    render: row =>
      renderTitle(
        contractRoute(row),
        row.contract.name || 'Untitled contract',
        row.contract.contract_type?.name
      ),
  },
  { title: 'Access', key: 'permissions', width: 200, render: row => renderAccess(row.permissions) },
];

const partyColumns: DataTableColumns<ContractualPartyUserShareInDb> = [
  {
    title: 'Party',
    key: 'contractual_party',
    render: row => renderTitle(partyRoute(row), row.contractual_party.name),
  },
  { title: 'Access', key: 'permissions', width: 200, render: row => renderAccess(row.permissions) },
];

/** Rows open what they list. */
function linkRow(to: RouteLocationRaw | null): Record<string, unknown> {
  if (!to) return {};
  return {
    style: { cursor: 'pointer' },
    onClick: (): void => {
      void router.push(to);
    },
  };
}

// ==================== Lifecycle ====================

onMounted(async () => {
  await fetchUser();
  const uid = user.value?.uid;
  await Promise.all([
    fetchRoles(),
    fetchUserGoogleDriveStatus(),
    ...(uid ? [fetchSharedContracts(uid), fetchSharedParties(uid)] : []),
  ]);
  await handleProfileOauthReturnFeedback();
});
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.page">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.profile }}</n-breadcrumb-item>
    </template>

    <n-spin :show="loading && !user">
      <template v-if="user">
        <!-- Header: who you are -->
        <n-flex vertical :size="16" :wrap="false" :style="{ marginBottom: '20px' }">
          <n-flex align="center" :size="14" :wrap="false" :style="{ minWidth: 0 }">
            <gaia-avatar :size="48">{{ initials }}</gaia-avatar>
            <n-flex vertical :size="4" :style="{ minWidth: 0, flex: '1 1 auto' }">
              <gaia-inline-edit
                v-if="editingName"
                :value="user.full_name"
                placeholder="Your name"
                :style="{ maxWidth: '360px' }"
                @commit="commitName"
                @cancel="editingName = false"
              />
              <n-flex v-else align="center" :size="8" :wrap="false">
                <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
                  {{ displayName }}
                </gaia-header>
                <gaia-icon-button
                  :dimension="28"
                  quaternary
                  title="Edit name"
                  :loading="savingName"
                  :disabled="savingName"
                  @click="editingName = true"
                >
                  <n-icon :size="15" :component="IconEdit" />
                </gaia-icon-button>
              </n-flex>
              <gaia-text size="body" tone="subtle">
                {{ user.full_name ? `${user.email} · ` : '' }}Member since
                {{ formatDateForDisplay(user.created_at ?? null) }}
              </gaia-text>
            </n-flex>
          </n-flex>
          <gaia-tab-bar
            :items="tabs"
            :active="currentTab"
            @select="(key: string) => (activeTab = key)"
          />
        </n-flex>

        <!-- Profile: your own settings -->
        <n-flex v-if="currentTab === 'profile'" vertical :size="16" :wrap="false">
          <gaia-card>
            <template #header>Integrations</template>
            <n-flex justify="space-between" align="center" :size="16" :wrap="false">
              <n-flex align="center" :size="12" :wrap="false" :style="{ minWidth: 0 }">
                <n-icon :component="IconGoogle" :size="22" />
                <n-flex vertical :size="2" :style="{ minWidth: 0 }">
                  <n-flex align="center" :size="8" :wrap="false">
                    <gaia-text size="body" weight="semibold" tone="ink">Google Drive</gaia-text>
                    <gaia-status-chip
                      v-if="!googleDriveLoading"
                      :tone="isGoogleDriveConnected ? 'success' : 'neutral'"
                    >
                      {{ isGoogleDriveConnected ? 'Connected' : 'Not connected' }}
                    </gaia-status-chip>
                  </n-flex>
                  <gaia-text size="control" tone="subtle">
                    {{
                      isGoogleDriveConnected && googleDriveStatus?.authorized_at
                        ? `Connected on ${formatDateForDisplay(googleDriveStatus.authorized_at)}`
                        : 'Connect your personal Google Drive to your account.'
                    }}
                  </gaia-text>
                </n-flex>
              </n-flex>
              <gaia-popconfirm
                v-if="isGoogleDriveConnected"
                title="Disconnect Google Drive?"
                confirm-label="Disconnect"
                @confirm="handleUserGoogleDriveDisconnect"
              >
                <template #trigger>
                  <gaia-button size="small" :loading="googleDriveActionLoading">
                    Disconnect
                  </gaia-button>
                </template>
              </gaia-popconfirm>
              <gaia-button
                v-else
                type="primary"
                size="small"
                :loading="googleDriveActionLoading"
                :disabled="googleDriveLoading"
                @click="googleDriveNoticeModal?.show()"
              >
                Connect
              </gaia-button>
            </n-flex>
          </gaia-card>

          <gaia-card>
            <template #header>Tutorials</template>
            <n-flex justify="space-between" align="center" :size="16" :wrap="false">
              <gaia-text size="control" tone="subtle">
                Show the onboarding tours again on this device.
              </gaia-text>
              <gaia-popconfirm
                title="Reset tutorials?"
                confirm-label="Reset"
                @confirm="handleResetTutorials"
              >
                <template #trigger>
                  <gaia-button size="small" :loading="tutorialResetLoading">
                    Reset tutorials
                  </gaia-button>
                </template>
                Completed and skipped tours will show again.
              </gaia-popconfirm>
            </n-flex>
          </gaia-card>
        </n-flex>

        <!-- Roles you hold in the active organisation -->
        <template v-else-if="currentTab === 'roles'">
          <gaia-empty-state v-if="roles.length === 0 && !loadingRoles" title="No roles yet" dashed>
            <template #icon>
              <n-icon :component="IconShieldCheck" :size="18" />
            </template>
            Roles decide what you can do in the organisation. An admin can assign you one.
          </gaia-empty-state>
          <template v-else>
            <gaia-text size="control" tone="subtle" tag="p" :style="{ margin: '0 0 12px' }">
              You can do everything these roles allow.
            </gaia-text>
            <gaia-table
              :columns="roleColumns"
              :data="roles"
              :loading="loadingRoles"
              :row-key="(row: PermissionRoleInDb) => row.uid"
              :row-props="(row: PermissionRoleInDb) => linkRow(roleRoute(row))"
            />
          </template>
        </template>

        <!-- Contracts shared with you directly -->
        <template v-else-if="currentTab === 'contracts'">
          <gaia-empty-state
            v-if="sharedContracts.length === 0 && !loadingContracts"
            title="No contracts shared with you"
            dashed
          >
            <template #icon>
              <n-icon :component="IconDocumentText" :size="18" />
            </template>
            Contracts shared with you personally show up here. Access through your roles is not
            listed.
          </gaia-empty-state>
          <template v-else>
            <gaia-text size="control" tone="subtle" tag="p" :style="{ margin: '0 0 12px' }">
              Shared with you personally, on top of what your roles give you.
            </gaia-text>
            <gaia-table
              :columns="contractColumns"
              :data="sharedContracts"
              :loading="loadingContracts"
              :row-key="(row: ContractUserShareInDb) => row.contract.uid"
              :row-props="(row: ContractUserShareInDb) => linkRow(contractRoute(row))"
            />
          </template>
        </template>

        <!-- Contractual parties shared with you directly -->
        <template v-else-if="currentTab === 'parties'">
          <gaia-empty-state
            v-if="sharedParties.length === 0 && !loadingParties"
            title="No parties shared with you"
            dashed
          >
            <template #icon>
              <n-icon :component="IconPeople" :size="18" />
            </template>
            Contractual parties shared with you personally show up here. Access through your roles
            is not listed.
          </gaia-empty-state>
          <template v-else>
            <gaia-text size="control" tone="subtle" tag="p" :style="{ margin: '0 0 12px' }">
              Shared with you personally, on top of what your roles give you.
            </gaia-text>
            <gaia-table
              :columns="partyColumns"
              :data="sharedParties"
              :loading="loadingParties"
              :row-key="(row: ContractualPartyUserShareInDb) => row.contractual_party.uid"
              :row-props="(row: ContractualPartyUserShareInDb) => linkRow(partyRoute(row))"
            />
          </template>
        </template>

        <!-- Organisations: memberships come from the profile the auth store
             already holds; leaving updates it from the response. -->
        <organisation-membership-list
          v-else-if="currentTab === 'organisations'"
          :organisations="authStore.organisations"
          :active-uid="authStore.backendUser?.active_organisation?.uid ?? null"
          :busy-uid="busyOrganisationUid"
          @switch="switchToOrganisation"
          @leave="askToLeave"
        >
          <template v-if="authStore.userPermissions.can_create_organisation" #actions>
            <gaia-create-button subject="organisation" @click="showCreateOrganisation = true" />
          </template>
        </organisation-membership-list>

        <!-- Vault folders and files shared with you directly. Kept mounted
             (v-show) so the tab shows its count before it is opened. -->
        <div v-if="user" v-show="currentTab === 'files'">
          <vault-shares-table
            :subject="{ kind: 'user', uid: user.uid }"
            subject-name="you"
            @count="(count: number) => (sharedVaultItemCount = count)"
          />
        </div>
      </template>

      <gaia-empty-state v-else-if="!loading" title="Couldn’t load your profile">
        Refresh the page to try again.
      </gaia-empty-state>
    </n-spin>

    <google-drive-verification-notice-modal
      ref="googleDriveNoticeModal"
      @confirm="handleUserGoogleDriveConnect"
    />

    <organisation-severance-modal
      mode="leave"
      :show="!!organisationPendingLeave"
      :organisation-name="organisationPendingLeave?.name ?? ''"
      :loading="leaving"
      :error="leaveError"
      @update:show="(open: boolean) => !open && (organisationPendingLeave = null)"
      @confirm="confirmLeave"
    />
    <create-organisation-modal v-model:show="showCreateOrganisation" />
  </Layout>
</template>
